import Button from "@common/controls/Button/component";
import Hint from "@common/controls/Hint/component";
import { System } from "@common/declarations";
import { type FetchOptionsArgs, type FetchOptionsResult } from "@common/hocs/withLazy";
import { useI18n } from "@common/hooks/i18n";
import { useUpdated } from "@common/lib/hooks";
import { joinDefined, uuid } from "@common/lib/misc";
import React from "react";
import { getApi as getSystemApi } from "../../modules/system/api";
import { UserProfilePicture } from "../ProfilePicture/component";
import { type ActivityItem, type ActivityProps, LazyActivity } from "./component";
import { mapHistoryItem } from "./lib";

export type GetHistoryArgs = FetchOptionsArgs & {
  historyTypeCategoryKey?: string;
};
export type GetHistory = (args: GetHistoryArgs) => Promise<System.Api.HistoryItem[]>;

export type HistoryActivityProps = Omit<ActivityProps<any>, "options"> & {
  category?: string;
  dashboardKey?: string;
  get?: GetHistory;
  labelKeyMapper?: (labelKey: string) => string;
  postMap?: (mapped: ActivityItem, item: System.Api.HistoryItem) => ActivityItem;
  obfuscateAmounts?: boolean;
  refreshKey?: string | null;
  showLink?: boolean;
  showRefresh?: boolean;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  imageSize?: number | string;
};

const HistoryActivity = ({
  category,
  get,
  postMap,
  obfuscateAmounts,
  showLink,
  refreshKey,
  showRefresh,
  dashboardKey,
  endDate,
  startDate,
  ...props
}: HistoryActivityProps) => {
  const i18n = useI18n();

  const fetchOptions = React.useCallback(
    async (args: FetchOptionsArgs): Promise<FetchOptionsResult<ActivityItem>> => {
      const requestData = {
        ...args,
        historyTypeCategoryKey: category,
        dashboardKey,
        endDate,
        startDate
      };
      const items = await (get ?? getSystemApi(i18n).getHistory)(requestData);

      return {
        items: items.map<ActivityItem>((x) => {
          const mapped = mapHistoryItem(i18n, x, showLink ?? true, obfuscateAmounts, props.labelKeyMapper);
          if (x.historyTypeKey === "Comment") {
            mapped.icon = (
              <UserProfilePicture
                firstName={x.createdByUserFirstName}
                lastName={x.createdByUserLastName}
                email={null}
                profilePictureMediaSourceId={x.userProfilePictureMediaSourceId}
                profilePictureMediaSourceKey={x.userProfilePictureMediaSourceKey}
                size={props.imageSize}
              />
            );
          }
          if (x.historyTypeKey === "Comment" || x.historyTypeKey === "Note") {
            mapped.title = (
              <div className="firc fg sm">
                {mapped.title}
                <Hint hint={x.createdByUserIsContact ? i18n.contact : i18n.staff} />
              </div>
            );
          }
          return postMap ? postMap(mapped, x) : mapped;
        }),
        hasMore: items.length === args.size
      };
    },
    [i18n, category, obfuscateAmounts, showLink, dashboardKey, startDate, endDate, postMap]
  );

  const [key, setKey] = React.useState(
    joinDefined(" ", category, startDate?.toString(), endDate?.toString()) || "loader"
  );

  useUpdated(
    () => setKey(joinDefined(" ", category, startDate?.toString(), endDate?.toString()) || "loader"),
    [category, endDate, startDate]
  );

  const onRefreshClick = React.useCallback(() => setKey(uuid()), [setKey]);

  const actions = React.useMemo(() => {
    const actions = Array.isArray(props.actions) ? props.actions.slice() : [props.actions];
    if (showRefresh) {
      actions.splice(
        0,
        0,
        <Button key="refresh" onClick={onRefreshClick} type="white">
          {i18n.refresh}
        </Button>
      );
    }
    return actions.some((x) => x) ? actions : undefined;
  }, [onRefreshClick, showRefresh, props.actions]);

  return <LazyActivity {...props} actions={actions} key={key} refreshKey={refreshKey} fetchOptions={fetchOptions} />;
};
export default HistoryActivity;
